import { AlertTriangle, CheckCircle2, CircleDashed, Info, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cx } from '../cx.js';

/**
 * Status, said twice.
 *
 * Every status in this system is an icon **and** a word. Never a coloured dot
 * on its own, never a red cell, never a green tick with no label. Around one
 * man in twelve cannot separate the red from the green, and a salon owner
 * reading their dashboard on a phone in daylight is in much the same position.
 * §44: colour is never the only carrier of meaning.
 *
 * Four states, which is as many as anyone can hold at once. `neutral` is for
 * "nothing has happened yet" and is deliberately not a warning — an
 * establishment that has taken no calls today has no problem.
 */

export type StatusKind = 'ok' | 'warn' | 'error' | 'neutral' | 'live';

const ICONS: Record<StatusKind, typeof CheckCircle2> = {
  ok: CheckCircle2,
  warn: AlertTriangle,
  error: XCircle,
  neutral: CircleDashed,
  live: Info,
};

const TONES: Record<StatusKind, string> = {
  ok: 'text-success',
  warn: 'text-warning',
  error: 'text-danger',
  neutral: 'text-ink-muted',
  live: 'text-teal-ink',
};

const PILLS: Record<StatusKind, string> = {
  ok: 'bg-success-wash text-success',
  warn: 'bg-warning-wash text-warning',
  error: 'bg-danger-wash text-danger',
  neutral: 'bg-surface-muted text-ink-muted',
  live: 'bg-teal-wash text-teal-ink',
};

export function StatusIndicator({
  kind,
  children,
  pill = false,
}: {
  readonly kind: StatusKind;
  readonly children: ReactNode;
  readonly pill?: boolean;
}): React.JSX.Element {
  const Icon = ICONS[kind];
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 text-ui-sm font-medium',
        pill ? cx('rounded-pill px-2.5 py-1', PILLS[kind]) : TONES[kind],
      )}
    >
      <Icon size={16} strokeWidth={1.75} aria-hidden="true" className="shrink-0" />
      {children}
    </span>
  );
}

/**
 * A call happening right now.
 *
 * The one place a pulse is allowed, because it is the one thing on the screen
 * that is genuinely still moving. `aria-live` is deliberately absent: a
 * screen reader announcing "en cours" on a loop while somebody is reading the
 * rest of the page is worse than silence, and the duration beside it already
 * carries the state.
 */
export function LiveBadge({
  children = 'En direct',
}: {
  readonly children?: ReactNode;
}): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-2 rounded-pill bg-teal-wash px-2.5 py-1 text-caption font-medium uppercase tracking-wide text-teal-ink">
      <span className="relative flex h-2 w-2" aria-hidden="true">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-pill bg-teal opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-pill bg-teal-ink" />
      </span>
      {children}
    </span>
  );
}

/**
 * What a finished call came to.
 *
 * Reads from the server's stable outcome code, never from a label: a copy
 * change must not be able to turn a booking into a failure. An unknown code
 * shows as itself rather than being forced into one of the buckets — a new
 * outcome the server learned about should look unfamiliar, not wrong.
 */
const OUTCOME_KIND: Readonly<Record<string, StatusKind>> = {
  booked: 'ok',
  rescheduled: 'ok',
  cancelled: 'neutral',
  information_provided: 'ok',
  transferred: 'neutral',
  voicemail: 'neutral',
  abandoned: 'warn',
  failed: 'error',
};

export function CallStatus({
  outcome,
  label,
}: {
  readonly outcome: string | null;
  readonly label: string;
}): React.JSX.Element {
  const kind: StatusKind = outcome === null ? 'neutral' : (OUTCOME_KIND[outcome] ?? 'neutral');
  return (
    <StatusIndicator kind={kind} pill>
      {label}
    </StatusIndicator>
  );
}
